import {
  ChevronDown,
  ChevronUp,
  Crown,
  ExternalLink,
  Lock,
  Trash2,
  Unlock,
} from 'lucide-react'
import { AuthoredIcon } from '@/components/admin/shared/authored-icon'
import { AutoSaveInput } from '@/components/ui/auto-save-input'
import { AutoSaveToggle } from '@/components/ui/auto-save-toggle'
import { Button } from '@/components/ui/button'
import { AUTO_SAVE_DEBOUNCE_MS } from '@/hooks/use-auto-save'
import type { ProblemEditorData } from '../problem-editor'
import type { ProblemSource } from '../types'
import type { SavableSourceField } from './sources-section'

export interface SortableSourceCardProps {
  source: ProblemSource
  isPrimary: boolean
  /**
   * Whether this is the row the learner sees credited as ORIGINAL — the
   * *resolved* answer, not the raw `isOriginal` column. An imported problem's
   * owning row is never marked, yet wins the slot through the ownership
   * fallback, so a card reading the column alone would show no original on the
   * very problems that have one.
   */
  isResolvedOriginal: boolean
  isExpanded: boolean
  onToggleExpand: () => void
  formData: ProblemEditorData
  /**
   * Record a keystroke locally, without saving it.
   *
   * Generic in the field so the key and the value stay correlated: the flat
   * `string | boolean | null` this replaced let `('isOriginal', 'yes')` and
   * `('order', null)` compile, and the value is spread straight onto the row.
   *
   * Only the debounced inputs use this. A failed save leaves their typed text
   * in place deliberately — `AutoSaveInput` shows the error and keeps the
   * value, so the admin can retry rather than watch their edit disappear. A
   * control that saves the moment it is touched must use `onCommitSourceField`
   * instead, which has an undo.
   */
  onLocalSourceUpdate: <K extends SavableSourceField>(
    sourceId: string,
    field: K,
    value: ProblemSource[K],
  ) => void
  /**
   * Apply, save, and — if the save is rejected — undo a field edit, as one
   * step. The immediate-save toggles use this instead of pairing
   * `onLocalSourceUpdate` with `onSaveSourceField`: marking an original also
   * clears whatever row held the mark, and only the owner of the list can put
   * that row back.
   */
  onCommitSourceField: <K extends SavableSourceField>(
    sourceId: string,
    field: K,
    value: ProblemSource[K],
  ) => Promise<void>
  /**
   * Save a field that only concerns its own row.
   *
   * `isOriginal` is excluded rather than documented: the two save props are
   * otherwise interchangeable at the call site, and picking this one for the
   * original toggle would save the mark without clearing the row that held it
   * — a compile error is the only thing that reliably stops that.
   *
   * Reports its own failure; call sites do not need a try/catch to toast.
   */
  onSaveSourceField: <K extends Exclude<SavableSourceField, 'isOriginal'>>(
    sourceId: string,
    field: K,
    value: ProblemSource[K],
  ) => Promise<void>
  onDeleteSource: (sourceId: string) => Promise<void>
}

export function SortableSourceCard({
  source,
  isPrimary,
  isResolvedOriginal,
  isExpanded,
  onToggleExpand,
  formData,
  onLocalSourceUpdate,
  onCommitSourceField,
  onSaveSourceField,
  onDeleteSource,
}: SortableSourceCardProps) {
  return (
    <div
      className={`group overflow-hidden rounded-lg border ${
        isPrimary ? 'border-primary/50 bg-primary/5' : 'hover:bg-muted/30'
      }`}
    >
      {/* Source header - always visible */}
      <div className="flex w-full items-center gap-3 px-4 py-3">
        <button
          type="button"
          onClick={onToggleExpand}
          className="flex flex-1 items-center gap-3 text-left"
        >
          <AuthoredIcon
            icon={source.sourceIcon}
            size="lg"
            className="flex-shrink-0"
            fallbackChip
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium">{source.source}</span>
              {isPrimary && <Crown className="h-4 w-4 text-yellow-500" />}
              {isResolvedOriginal && (
                <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-blue-700 dark:bg-blue-950 dark:text-blue-300">
                  ORIGINAL
                </span>
              )}
            </div>
            {source.title && source.title !== formData.name && (
              <p className="text-muted-foreground truncate text-sm">
                {source.title}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {source.externalId && (
              <span className="text-muted-foreground font-mono text-sm">
                #{source.externalId}
              </span>
            )}
            {source.difficulty && (
              <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
                {source.difficulty.name}
              </span>
            )}
            {source.premiumRequired ? (
              <Lock className="h-4 w-4 text-amber-500" />
            ) : (
              <Unlock className="text-muted-foreground h-4 w-4" />
            )}
            {source.url && (
              <a
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-foreground"
                onClick={(e) => e.stopPropagation()}
                aria-label="Open problem URL"
              >
                <ExternalLink className="h-4 w-4" />
              </a>
            )}
            {isExpanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </div>
        </button>
      </div>

      {/* Expanded edit form */}
      {isExpanded && (
        <div className="border-t px-4 py-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor={`source-title-${source.id}`}
                className="text-muted-foreground mb-1 block text-sm font-medium"
              >
                Title on this source
              </label>
              <AutoSaveInput
                id={`source-title-${source.id}`}
                type="text"
                value={source.title || ''}
                onChange={(newValue) =>
                  onLocalSourceUpdate(source.id, 'title', newValue)
                }
                onSave={(newValue) =>
                  onSaveSourceField(source.id, 'title', newValue)
                }
                debounceMs={AUTO_SAVE_DEBOUNCE_MS.slow}
                placeholder="Problem title"
                disabled={isPrimary}
              />
            </div>
            <div>
              <label
                htmlFor={`source-externalId-${source.id}`}
                className="text-muted-foreground mb-1 block text-sm font-medium"
              >
                External ID
              </label>
              <AutoSaveInput
                id={`source-externalId-${source.id}`}
                type="text"
                value={source.externalId || ''}
                onChange={(newValue) =>
                  onLocalSourceUpdate(source.id, 'externalId', newValue)
                }
                onSave={(newValue) =>
                  onSaveSourceField(source.id, 'externalId', newValue)
                }
                debounceMs={AUTO_SAVE_DEBOUNCE_MS.slow}
                placeholder="e.g., 1 or CH1-Q3"
                className="font-mono"
                disabled={isPrimary}
              />
            </div>
            <div>
              {/* A plain heading for the same reason as "Original source"
                  below: `AutoSaveToggle` renders no `id`, so the `label` this
                  replaced pointed `htmlFor` at nothing — the switch was
                  unnamed to a screen reader and the word was inert to click. */}
              <p className="text-muted-foreground mb-1 block text-sm font-medium">
                Premium
              </p>
              <AutoSaveToggle
                aria-label="Premium"
                checked={source.premiumRequired || false}
                // The commit rolls the optimistic update back on failure, so
                // the toggle's `checked` prop matches the persisted value —
                // otherwise `AutoSaveToggle`'s idle resync clobbers its own
                // error revert with the still-wrong prop.
                onSave={(newValue) =>
                  onCommitSourceField(source.id, 'premiumRequired', newValue)
                }
                className="origin-left scale-50"
                disabled={isPrimary}
              />
            </div>
            <div>
              {/* A plain heading, not a `label`: AutoSaveToggle takes no
                  `id`, so `htmlFor` would point at nothing and leave the
                  control unnamed. It names itself via `aria-label`. */}
              <p className="text-muted-foreground mb-1 block text-sm font-medium">
                {isPrimary ? 'Original by ownership' : 'Original source'}
              </p>
              <AutoSaveToggle
                aria-label={
                  isPrimary ? 'Original by ownership' : 'Original source'
                }
                // The owning row shows the *resolved* answer rather than its
                // column, which is always false for it. Showing the column
                // would read "not the original" on the imported problems where
                // the learner is being shown exactly that credit.
                checked={isPrimary ? isResolvedOriginal : source.isOriginal}
                // Committed rather than updated-then-saved: marking an
                // original also clears whichever row held the mark, and the
                // server rejects this save often enough to matter (a BCTCI or
                // soft-deleted source cannot be the external original). Undoing
                // both rows is the list owner's job, not this card's.
                onSave={(newValue) =>
                  onCommitSourceField(source.id, 'isOriginal', newValue)
                }
                className="origin-left scale-50"
                // The owning source has no ProblemSource row to carry the
                // flag, and needs none: a non-BCTCI owning source already
                // resolves as the original by fallback.
                disabled={isPrimary}
              />
              <p className="text-muted-foreground mt-1 text-xs">
                {isPrimary
                  ? 'Follows the owning source: a problem owned by anyone but BCTCI is credited to them automatically. Mark another source to override.'
                  : 'Where this problem first appeared. At most one per problem — marking this one clears the other.'}
              </p>
            </div>
            <div>
              <label
                htmlFor={`source-url-${source.id}`}
                className="text-muted-foreground mb-1 block text-sm font-medium"
              >
                Problem URL
              </label>
              <AutoSaveInput
                id={`source-url-${source.id}`}
                type="url"
                value={source.url || ''}
                onChange={(newValue) =>
                  onLocalSourceUpdate(source.id, 'url', newValue || null)
                }
                onSave={(newValue) =>
                  onSaveSourceField(source.id, 'url', newValue || null)
                }
                debounceMs={AUTO_SAVE_DEBOUNCE_MS.slow}
                placeholder="https://..."
                disabled={isPrimary}
              />
            </div>
            <div>
              <label
                htmlFor={`source-editorial-${source.id}`}
                className="text-muted-foreground mb-1 block text-sm font-medium"
              >
                Editorial URL
              </label>
              <AutoSaveInput
                id={`source-editorial-${source.id}`}
                type="url"
                value={source.editorialUrl || ''}
                onChange={(newValue) =>
                  onLocalSourceUpdate(
                    source.id,
                    'editorialUrl',
                    newValue || null,
                  )
                }
                onSave={(newValue) =>
                  onSaveSourceField(source.id, 'editorialUrl', newValue || null)
                }
                debounceMs={AUTO_SAVE_DEBOUNCE_MS.slow}
                placeholder="https://..."
                disabled={isPrimary}
              />
            </div>
          </div>
          <div className="mt-4 flex items-center justify-start">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onDeleteSource(source.id)}
              disabled={isPrimary}
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="mr-1 h-4 w-4" />
              Delete Source
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
